import { defineStore } from "pinia";
import { computed, ref, shallowRef, watch } from "vue";
import {
  ensemblePeriods,
  ensembleMatrix,
  type EnsembleInterval,
  type EnsembleQuantity,
  type EnsembleMatrixData,
  type EnsembleForecast,
  type EnsembleModel,
} from "rainowls-shared";
import { fetchEnsemble } from "@/services/ensemble.service";
import { useLocationStore } from "./location.store";
import { useSettingsStore } from "./settings.store";
import { useWeatherSessionStore } from "./weatherSession.store";

type ModelMode = EnsembleModel | "compare";

const CACHE_MS = 60 * 60_000;

export const useEnsembleStore = defineStore("ensembles", () => {
  const locations = useLocationStore();
  const settings = useSettingsStore();
  const session = useWeatherSessionStore();

  const active = ref(false);
  const mode = ref<ModelMode>("gfs05");
  const forecasts = shallowRef<
    Partial<Record<EnsembleModel, EnsembleForecast>>
  >({});
  const errors = ref<Partial<Record<EnsembleModel, string>>>({});
  const pending = ref<EnsembleModel[]>([]);

  const models = computed<EnsembleModel[]>(() =>
    mode.value === "compare" ? ["ecmwf_ifs025", "gfs05"] : [mode.value],
  );
  const quantity = ref<EnsembleQuantity | "rain-snow">("rain-snow");
  const interval = ref<EnsembleInterval>(24);
  const days = ref(15);
  const source = computed(() =>
    models.value.map((model) => forecasts.value[model]).find(Boolean),
  );
  const periods = computed(() =>
    ensemblePeriods(
      source.value?.timeZone ?? "UTC",
      days.value,
      interval.value,
      source.value?.fetchedAt ?? Date.now(),
      source.value?.times[0] ?? 0,
    ),
  );
  // Projections change only with forecast inputs, never hover or range selection.
  const matrices = computed(
    () =>
      Object.fromEntries(
        models.value.flatMap((model) => {
          const forecast = forecasts.value[model];
          if (!forecast) return [];
          const primary =
            quantity.value === "rain-snow" ? "rain" : quantity.value;
          return [
            [
              model,
              {
                primary: ensembleMatrix(forecast, primary, periods.value),
                snow:
                  quantity.value === "rain-snow"
                    ? ensembleMatrix(forecast, "snowfall", periods.value)
                    : undefined,
              },
            ],
          ];
        }),
      ) as Partial<
        Record<
          EnsembleModel,
          { primary: EnsembleMatrixData; snow?: EnsembleMatrixData }
        >
      >,
  );
  const loading = computed(() => pending.value.length > 0);
  const locationKey = computed(
    () => `${locations.displayLocation.lat}:${locations.displayLocation.lng}`,
  );

  // Cached results are keyed by location and model; only one request per model can run.
  const cache = new Map<string, EnsembleForecast>();
  const requests = new Map<
    EnsembleModel,
    { key: string; controller: AbortController; promise: Promise<void> }
  >();

  let stopWatch: (() => void) | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let shownLocation = "";

  /** Abort requests when the view closes or the selected location changes. */
  function cancel() {
    for (const request of requests.values()) {
      request.controller.abort();
    }
    requests.clear();
    pending.value = [];
  }

  /** Reuse a fresh result or the matching in-flight request for this model. */
  function load(model: EnsembleModel, force: boolean): Promise<void> {
    const key = `${locationKey.value}:${model}`;
    const inFlight = requests.get(model);
    if (inFlight?.key === key) {
      return inFlight.promise;
    }

    const cached = cache.get(key);
    if (!force && cached && Date.now() - cached.fetchedAt < CACHE_MS) {
      forecasts.value = { ...forecasts.value, [model]: cached };
      return Promise.resolve();
    }

    inFlight?.controller.abort();
    const controller = new AbortController();
    pending.value = [...new Set([...pending.value, model])];
    delete errors.value[model];

    const { lat, lng } = locations.displayLocation;
    const timeout = setTimeout(() => controller.abort(), 45_000);
    const promise = fetchEnsemble(lat, lng, model, controller.signal)
      .then((result) => {
        if (
          controller.signal.aborted ||
          locationKey.value !== shownLocation ||
          requests.get(model)?.controller !== controller
        ) {
          return;
        }
        cache.set(key, result);
        if (cache.size > 12) {
          cache.delete(cache.keys().next().value!);
        }
        forecasts.value = { ...forecasts.value, [model]: result };
      })
      .catch((error) => {
        if (requests.get(model)?.controller !== controller) {
          return;
        }

        if (controller.signal.aborted) {
          errors.value[model] = "The ensemble request timed out. Please retry.";
        } else {
          errors.value[model] =
            error instanceof Error
              ? error.message
              : "Unable to load this ensemble.";
        }
      })
      .finally(() => {
        clearTimeout(timeout);
        if (requests.get(model)?.controller === controller) {
          requests.delete(model);
          pending.value = pending.value.filter((m) => m !== model);
        }
      });
    requests.set(model, { key, controller, promise });
    return promise;
  }

  /** Load the models visible in the current mode for the selected location. */
  async function refresh(force = false) {
    if (!active.value || session.phase !== "ready") {
      return;
    }

    if (shownLocation !== locationKey.value) {
      cancel();
      shownLocation = locationKey.value;
      forecasts.value = {};
      errors.value = {};
    }

    await Promise.all(models.value.map((model) => load(model, force)));
  }

  /** Keep one refresh timer aligned with the user's interval preference. */
  function schedule() {
    clearTimeout(timer);
    if (!active.value || settings.autoRefreshInterval <= 0) {
      return;
    }

    timer = setTimeout(async () => {
      if (!document.hidden) await refresh();
      schedule();
    }, settings.autoRefreshInterval * 60_000);
  }

  /** Refresh when the Models page becomes visible again. */
  function visible() {
    if (!document.hidden) void refresh();
  }

  /** Watch only the inputs that can change ensemble data or polling. */
  function start() {
    if (stopWatch) {
      return;
    }

    stopWatch = watch(
      [
        active,
        locationKey,
        mode,
        () => session.phase,
        () => settings.autoRefreshInterval,
      ],
      () => {
        if (!active.value) {
          cancel();
        } else {
          void refresh();
        }
        schedule();
      },
      { immediate: true },
    );
    document.addEventListener("visibilitychange", visible);
  }

  /** Release the watcher, timer, and requests when leaving Models. */
  function stop() {
    stopWatch?.();
    stopWatch = undefined;
    clearTimeout(timer);
    cancel();
    document.removeEventListener("visibilitychange", visible);
  }

  /** Activate fetching only while the Models view needs ensemble data. */
  function setActive(value: boolean) {
    active.value = value;
  }

  /** Select one model or the side-by-side comparison. */
  function setMode(value: ModelMode) {
    mode.value = value;
  }

  return {
    quantity,
    interval,
    days,
    source,
    periods,
    matrices,
    mode,
    models,
    forecasts,
    errors,
    pending,
    loading,
    setActive,
    setMode,
    refresh,
    start,
    stop,
  };
});
